import React from 'react';
import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const bellyPulse = keyframes`
  0%, 100% { transform: translateX(-50%) scale(1); }
  50% { transform: translateX(-50%) scale(1.05); }
`;

const tailWag = keyframes`
  0%, 100% { transform: translateY(-50%) rotate(0deg); }
  25% { transform: translateY(-50%) rotate(-10deg); }
  75% { transform: translateY(-50%) rotate(10deg); }
`;

const blink = keyframes`
  0%, 49%, 51%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(0.1); }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.2); }
`;

const CatContainer = styled.div`
  text-align: center;
  padding: 50px 20px;
  position: relative;
`;

const ClickHint = styled.div`
  font-size: 1.5em;
  color: #ffe066;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  margin-bottom: 20px;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const CatWrapper = styled.div`
  position: relative;
  display: inline-block;
  animation: ${float} 3s ease-in-out infinite;
`;

const CatHead = styled.div`
  position: relative;
  width: 200px;
  height: 180px;
  background: linear-gradient(135deg, #ffa500 0%, #ff8c00 100%);
  border-radius: 50% 50% 45% 45%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    top: 80px;
    left: 50px;
    width: 30px;
    height: 20px;
    background: rgba(255, 192, 203, 0.6);
    border-radius: 50%;
    box-shadow: 70px 0 0 rgba(255, 192, 203, 0.6);
  }

  @media (max-width: 768px) {
    width: 150px;
    height: 140px;
  }

  @media (max-width: 480px) {
    width: 120px;
    height: 110px;
    
    &::before {
      top: 60px;
      left: 30px;
      width: 20px;
      height: 15px;
      box-shadow: 50px 0 0 rgba(255, 192, 203, 0.6);
    }
  }
`;

const CatEar = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  border-left: 40px solid transparent;
  border-right: 40px solid transparent;
  border-bottom: 60px solid #ffa500;
  top: -40px;

  &.left { 
    left: 20px; 
    transform: rotate(-15deg); 
  }
  
  &.right { 
    right: 20px; 
    transform: rotate(15deg); 
  }

  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-left: 25px solid transparent;
    border-right: 25px solid transparent;
    border-bottom: 40px solid #ffb84d;
    top: 10px;
    left: -25px;
  }

  @media (max-width: 480px) {
    border-left: 30px solid transparent;
    border-right: 30px solid transparent;
    border-bottom: 45px solid #ffa500;
    top: -30px;

    &::after {
      border-left: 20px solid transparent;
      border-right: 20px solid transparent;
      border-bottom: 30px solid #ffb84d;
    }
  }
`;

const CatEye = styled.div`
  position: absolute;
  width: 30px;
  height: 35px;
  background: linear-gradient(135deg, #00ff00, #00cc00);
  border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
  top: 60px;
  box-shadow: inset 0 -8px 10px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 255, 0, 0.5);
  animation: ${blink} 4s infinite;

  &.left { left: 50px; }
  &.right { right: 50px; }

  &::before {
    content: '';
    position: absolute;
    width: 15px;
    height: 20px;
    background: #000;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &::after {
    content: '✨';
    position: absolute;
    top: -5px;
    right: -5px;
    font-size: 12px;
    animation: ${sparkle} 2s infinite;
  }

  @media (max-width: 480px) {
    width: 22px;
    height: 26px;
    top: 45px;

    &.left { left: 30px; }
    &.right { right: 30px; }

    &::before {
      width: 12px;
      height: 16px;
    }

    &::after {
      font-size: 10px;
      top: -4px;
      right: -4px;
    }
  }
`;

const CatNose = styled.div`
  position: absolute;
  width: 20px;
  height: 15px;
  background: #ff69b4;
  border-radius: 50% 50% 50% 50% / 40% 40% 60% 60%;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);

  &::after {
    content: '';
    position: absolute;
    width: 2px;
    height: 15px;
    background: #ff69b4;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: 480px) {
    width: 16px;
    height: 12px;
    bottom: 30px;

    &::after {
      height: 12px;
      bottom: -12px;
    }
  }
`;

const CatWhisker = styled.div`
  position: absolute;
  height: 2px;
  background: #333;
  top: 100px;

  &.wl1 { width: 60px; left: -60px; transform: rotate(-10deg); }
  &.wl2 { width: 70px; left: -70px; }
  &.wl3 { width: 60px; left: -60px; transform: rotate(10deg); top: 115px; }
  &.wr1 { width: 60px; right: -60px; transform: rotate(10deg); }
  &.wr2 { width: 70px; right: -70px; }
  &.wr3 { width: 60px; right: -60px; transform: rotate(-10deg); top: 115px; }

  @media (max-width: 480px) {
    height: 1.5px;
    top: 75px;

    &.wl1 { width: 45px; left: -45px; }
    &.wl2 { width: 55px; left: -55px; }
    &.wl3 { width: 45px; left: -45px; top: 85px; }
    &.wr1 { width: 45px; right: -45px; }
    &.wr2 { width: 55px; right: -55px; }
    &.wr3 { width: 45px; right: -45px; top: 85px; }
  }
`;

const CatBody = styled.div`
  position: relative;
  width: 250px;
  height: 200px;
  background: linear-gradient(135deg, #ffa500 0%, #ff8c00 100%);
  border-radius: 50% 50% 50% 50% / 40% 40% 60% 60%;
  margin-top: -40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  z-index: 1;

  @media (max-width: 768px) {
    width: 180px;
    height: 150px;
    margin-top: -30px;
  }

  @media (max-width: 480px) {
    width: 140px;
    height: 120px;
    margin-top: -25px;
  }
`;

const CatBelly = styled.div`
  position: absolute;
  width: 120px;
  height: 140px;
  background: linear-gradient(135deg, #ffb84d 0%, #ffa500 100%);
  border-radius: 50%;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
  transition: all 0.3s;
  animation: ${bellyPulse} 2s ease-in-out infinite;
  box-shadow: inset 0 0 30px rgba(255, 255, 255, 0.3);

  &::before {
    content: '❤️';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 40px;
    opacity: 0.7;
  }

  &:hover {
    transform: translateX(-50%) scale(1.1);
    box-shadow: inset 0 0 40px rgba(255, 255, 255, 0.5), 0 0 20px rgba(255, 184, 77, 0.8);
  }

  &:active {
    transform: translateX(-50%) scale(0.9);
  }

  @media (max-width: 768px) {
    width: 90px;
    height: 100px;
  }

  @media (max-width: 480px) {
    width: 70px;
    height: 80px;

    &::before {
      font-size: 30px;
    }
  }
`;

const CatLeg = styled.div`
  position: absolute;
  width: 50px;
  height: 80px;
  background: linear-gradient(to bottom, #ffa500, #ff8c00);
  border-radius: 20px 20px 40% 40%;
  bottom: -75px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);

  &.leg1 { left: 30px; }
  &.leg2 { left: 90px; }
  &.leg3 { right: 90px; }
  &.leg4 { right: 30px; }

  &::after {
    content: '';
    position: absolute;
    width: 60px;
    height: 25px;
    background: #ffa500;
    border-radius: 50%;
    bottom: -5px;
    left: -5px;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 60px;
    bottom: -55px;

    &.leg1 { left: 25px; }
    &.leg2 { left: 70px; }
    &.leg3 { right: 70px; }
    &.leg4 { right: 25px; }
  }

  @media (max-width: 480px) {
    width: 35px;
    height: 50px;
    bottom: -45px;

    &.leg1 { left: 20px; }
    &.leg2 { left: 55px; }
    &.leg3 { right: 55px; }
    &.leg4 { right: 20px; }
  }
`;

const CatTail = styled.div`
  position: absolute;
  width: 120px;
  height: 30px;
  background: linear-gradient(to right, #ffa500, #ff8c00);
  border-radius: 50% 90% 90% 50%;
  right: -100px;
  top: 50%;
  transform: translateY(-50%);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  animation: ${tailWag} 2s ease-in-out infinite;
  transform-origin: left center;

  @media (max-width: 768px) {
    width: 90px;
    height: 25px;
    right: -75px;
  }

  @media (max-width: 480px) {
    width: 75px;
    height: 20px;
    right: -60px;
  }
`;

function Cat({ onFart }) {
  const handleBellyClick = (e) => {
    const belly = e.currentTarget;
    const rect = belly.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    
    onFart(x, y);
    
    // Animate belly
    belly.style.transform = 'translateX(-50%) scale(0.9)';
    setTimeout(() => {
      belly.style.transform = 'translateX(-50%) scale(1)';
    }, 200);
  };

  return (
    <CatContainer>
      <ClickHint>👇 CLICK THE BELLY! 👇</ClickHint>
      <CatWrapper>
        <CatHead>
          <CatEar className="left" />
          <CatEar className="right" />
          <CatEye className="left" />
          <CatEye className="right" />
          <CatNose />
          <CatWhisker className="wl1" />
          <CatWhisker className="wl2" />
          <CatWhisker className="wl3" />
          <CatWhisker className="wr1" />
          <CatWhisker className="wr2" />
          <CatWhisker className="wr3" />
        </CatHead>
        <CatBody>
          <CatBelly onClick={handleBellyClick} />
        </CatBody>
        <CatLeg className="leg1" />
        <CatLeg className="leg2" />
        <CatLeg className="leg3" />
        <CatLeg className="leg4" />
        <CatTail />
      </CatWrapper>
    </CatContainer>
  );
}

export default Cat;
